import React from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  position: relative;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const TextContent = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-top: 16px;
  margin-bottom: 16px;
  text-align: center;
  max-width: 80%; /* 20% skinnier than 100% */
  margin-left: auto;
  margin-right: auto;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const StudyBoxContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto ${theme.spacing.xl} auto;
  max-width: 800px;
  padding: 0 ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0 ${theme.spacing.sm};
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StudyBox = styled.div`
  background: ${theme.colors.doryGreen}; /* Green Dory brand color background */
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.lg};
  border: 2px solid ${theme.colors.doryGreen};
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out forwards;
  animation-delay: 200ms;
  opacity: 0;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  position: relative;
  overflow: hidden;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.doryBlue};
  }
  
  /* Add a subtle gradient overlay for depth */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg};
  }
`;

const StudyIcon = styled.div`
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.md} auto;
  position: relative;
  z-index: 1;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }
  
  /* Fallback styling when no image - show emoji without circular background */
  &:not(:has(img)) {
    font-size: 5rem;
    color: white;
    
    &::after {
      content: '${props => props.icon || '🔬'}';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
    }
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 140px;
    height: 140px;
    margin-bottom: ${theme.spacing.sm};
    
    &:not(:has(img)) {
      font-size: 4rem;
    }
  }
`;

const StudyImage = styled.img`
  width: 130px;
  height: 130px;
  object-fit: contain;
  object-position: center;
  display: block;
  margin: 0 auto ${theme.spacing.md} auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 104px;
    height: 104px;
    margin-bottom: ${theme.spacing.sm};
  }
`;

const StudyTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: white; /* White text on dark background */
  margin: 0 0 ${theme.spacing.md} 0;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.h3};
    margin-bottom: ${theme.spacing.sm};
  }
`;

const StudyDescription = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: rgba(255, 255, 255, 0.9); /* Slightly transparent white for better readability */
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.small};
  }
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px; /* Ensure good touch target size */
  box-shadow: ${theme.shadows.sm};
  margin-top: 0;
  margin-bottom: 24px;
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem 2.0rem 1rem;
    min-width: 150px;
  }
`;

const StudyDetailsScreen = ({ data, onNext, userProgress = {} }) => {
  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content for the description text
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Default study box content if none provided in content
  const defaultStudyBox = {
    icon: '🔬',
    title: 'Clinical Trial Backed Research',
    description: 'Our tools and insights are powered by rigorous clinical trial research, ensuring evidence-based approaches to mental performance optimization.',
    color: theme.colors.doryBlue
  };

  // Use content study box if provided, otherwise use default
  const studyBox = data.content?.studyBox || defaultStudyBox;

  return (
    <Container>
      <Content>
        {/* Study box */}
        <StudyBoxContainer>
          <StudyBox>
            {/* Study image - inside the feature box above the text */}
            {studyBox.image && (
              <StudyImage 
                src={studyBox.image} 
                alt={studyBox.title || 'Study Research Icon'} 
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <StudyTitle>{studyBox.title}</StudyTitle>
            <StudyDescription>{studyBox.description}</StudyDescription>
          </StudyBox>
        </StudyBoxContainer>
        
        {/* Description text */}
        {htmlContent && (
          <TextContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* CTA button */}
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default StudyDetailsScreen;
